import { ChangeDetectorRef, Component, effect, Input, OnDestroy, inject } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DownPaymentPayload } from '@app/core/models/initialize-response.model';
import { CustomerErrorCode } from '@app/sales/constants/ProblemCodes';
import { CustomerDownpaymentCheckAction } from '@app/sales/modules/details/states/customer.actions';
import { CustomerSelectors } from '@app/sales/modules/details/states/customer.selectors';
import { NotDNumberValidator } from '@app/sales/validators/d-number.validator';
import { noIllegalCharactersValidator } from '@app/sales/validators/no-illegal-characters-validator';
import { ssnValidator } from '@app/sales/validators/ssn.validator';
import { IconNames } from '@app/shared/enums/icon-names';
import { select, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { SpinnerComponent } from '../atoms/spinner/spinner.component';
import { IceButtonComponent } from '../atoms/ice-button/ice-button.component';
import { IceInputFieldComponent } from '../molecules/ice-input-field/ice-input-field.component';
import { NgClass } from '@angular/common';
import { IceAlertComponent } from '../molecules/ice-alert/ice-alert.component';

@Component({
  selector: 'app-extended-credit-check',
  templateUrl: './extended-credit-check.component.html',
  styleUrl: './extended-credit-check.component.scss',
  imports: [
    IceAlertComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    IceInputFieldComponent,
    IceButtonComponent,
    SpinnerComponent,
  ],
})
export class ExtendedCreditCheckComponent implements OnDestroy {
  private formBuilder = inject(FormBuilder);
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);

  IconNames = IconNames;

  @Input() size: 'small' | 'regular' = 'regular';

  customer = select(CustomerSelectors.getState);
  isValidCustomer = select(CustomerSelectors.isValidCustomer);
  customerErrorCode = select(CustomerSelectors.customerErrorCode);

  subscriptions$: Subscription[] = [];

  errorInAllowanceCheck = false;

  customerCreditCheckForm = this.formBuilder.group({
    firstname: this.formBuilder.control('', [Validators.required, noIllegalCharactersValidator()]),
    lastname: this.formBuilder.control('', [Validators.required, noIllegalCharactersValidator()]),
    ssn: this.formBuilder.control('', [
      Validators.required,
      Validators.minLength(11),
      Validators.maxLength(11),
      ssnValidator(),
      NotDNumberValidator(),
    ]),
  });

  constructor() {
    effect(() => {
      if (!!this.customer() && !this.customer().isLoadingCustomer && !this.customer().isLoadingCustomerDownPaymentCheck) {
        const { firstname, lastname, ssn } = this.customerCreditCheckForm.controls;
        this.customerCreditCheckForm.setValue({
          firstname: this.customer().privateCustomer.firstname || firstname.value,
          lastname: this.customer().privateCustomer.lastname || lastname.value,
          ssn: this.customer().privateCustomer.ssn || ssn.value,
        });

        this.errorInAllowanceCheck =
          (this.customer().hasPerformedAllowanceCheck && !this.isValidCustomer()) ||
          (this.customer().hasPerformedAllowanceCheck &&
            this.isValidCustomer() &&
            this.customer().authorizeResult.subscriptionAllowance &&
            !this.customer().authorizeResult.subscriptionAllowance.approved);
      }

      this.changeDetectorRef.detectChanges();
    });
  }

  runCreditCheck() {
    if (this.customerCreditCheckForm.invalid) {
      return;
    }

    const firstname = this.customerCreditCheckForm.get('firstname').value;
    const lastname = this.customerCreditCheckForm.get('lastname').value;
    const ssn = this.customerCreditCheckForm.get('ssn').value;
    const payload: DownPaymentPayload = {
      firstName: firstname,
      lastName: lastname,
      ssn: ssn,
      customerId: this.customer().customerId || null,
    };
    this.store.dispatch(new CustomerDownpaymentCheckAction(payload));
  }

  get hasSsnMismatchError(): boolean {
    return this.customerErrorCode() === CustomerErrorCode.SSN_MISMATCH;
  }

  get showCustomerError(): boolean {
    return !!this.customer()?.customerError || this.errorInAllowanceCheck;
  }

  get showCreditCheckForm(): boolean {
    return this.hasSsnMismatchError || (!this.customer()?.hasPerformedDetailedCreditCheck && !this.showCustomerError);
  }

  get displayCreditCheckError() {
    return (
      this.errorInAllowanceCheck ||
      this.customer()?.authorizeResult?.reason ||
      this.customer()?.authorizeResult?.subscriptionAllowance?.reason ||
      (this.customer()?.isExistingCustomer && this.customer().authorizeResult?.downPaymentAllowance?.reason)
    );
  }

  get creditFreezeError() {
    return this.customer()?.isExistingCustomer && this.customer()?.authorizeResult.downPaymentAllowance?.reason;
  }

  get isCustomerNameInvalid(): boolean {
    return this.customerCreditCheckForm.get('firstname').invalid || this.customerCreditCheckForm.get('lastname').invalid;
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(sub => sub.unsubscribe());
  }
}
